import PropTypes from 'prop-types'
import React from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { Toggle } from '@/components/forms/Toggle'

/**
 * The controls an RO table row works with (WARP-951, WARP-966), shared by the Work Queue
 * and the dashboard's RO list so a row reads and behaves the same in both. `size` is
 * `md` in the Work Queue's full-width table and `sm` in the dashboard's side panel.
 */

const CHIP_SIZES = {
  md: 'px-3 py-1 text-xs',
  sm: 'px-2 py-0.5 text-[11px]',
}

/**
 * The next statuses as radio chips. Clicking the chosen chip again clears it, since a
 * radio group has no other way back to "nothing picked".
 */
export const StatusChips = ({ choice, disabled, name, onChoose, options, roNumber, size }) => (
  <fieldset className="flex flex-wrap gap-1" disabled={disabled}>
    <legend className="sr-only">Status for RO {roNumber}</legend>
    {options.map((option) => {
      const checked = choice === option.value
      return (
        <label
          className={mergeClassNames(
            'cursor-pointer select-none whitespace-nowrap rounded-full border font-semibold transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-800',
            CHIP_SIZES[size],
            checked
              ? 'border-blue-800 bg-blue-800 text-white'
              : 'border-charcoal-200 text-charcoal-700 bg-white hover:border-blue-800',
            disabled && 'hover:border-charcoal-200 cursor-not-allowed opacity-60',
          )}
          key={option.value}
        >
          <input
            checked={checked}
            className="sr-only"
            name={name}
            onChange={() => {}}
            onClick={() => onChoose(checked ? null : option.value)}
            type="radio"
            value={option.value}
          />
          {option.label}
        </label>
      )
    })}
  </fieldset>
)

StatusChips.propTypes = {
  choice: PropTypes.string,
  disabled: PropTypes.bool,
  // The radio group's name, unique per row.
  name: PropTypes.string.isRequired,
  onChoose: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }))
    .isRequired,
  roNumber: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['md', 'sm']),
}

StatusChips.defaultProps = {
  choice: null,
  disabled: false,
  size: 'md',
}

/**
 * The dealer-closed flag, settable at any stage so a missed one can be fixed (Michelle,
 * 2026-10-08). Changed here, it waits for the row's Save like everything else on it.
 */
export const DealerClosedSwitch = ({ checked, disabled, onChange, roNumber }) => (
  <Toggle
    aria-label={`Dealer closed for RO ${roNumber}`}
    checked={checked}
    disabled={disabled}
    label=""
    // Rows are white, where the default white track disappears.
    offTrackClassName="bg-charcoal-200"
    onChange={onChange}
  />
)

DealerClosedSwitch.propTypes = {
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  roNumber: PropTypes.string.isRequired,
}

DealerClosedSwitch.defaultProps = {
  disabled: false,
}

const SAVE_SIZES = {
  md: 'px-3 py-1.5 text-xs',
  sm: 'px-2 py-0.5 text-[10px]',
}

/** The row's own Save. Enabled only when the row holds something to save. */
export const RowSaveButton = ({ changed, disabled, onSave, roNumber, saving, size }) => (
  <button
    aria-label={`Save RO ${roNumber}`}
    className={mergeClassNames(
      'disabled:border-charcoal-200 disabled:text-charcoal-300 whitespace-nowrap rounded border-[1.5px] border-blue-800 font-bold uppercase tracking-widest text-blue-800 transition-colors hover:bg-blue-800 hover:text-white disabled:cursor-not-allowed disabled:hover:bg-transparent',
      SAVE_SIZES[size],
    )}
    disabled={disabled || saving || !changed}
    onClick={onSave}
    type="button"
  >
    {saving ? 'Saving…' : 'Save'}
  </button>
)

RowSaveButton.propTypes = {
  changed: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  roNumber: PropTypes.string.isRequired,
  saving: PropTypes.bool,
  size: PropTypes.oneOf(['md', 'sm']),
}

RowSaveButton.defaultProps = {
  disabled: false,
  saving: false,
  size: 'md',
}
